import React from 'react';
import { motion } from 'framer-motion';
import { Crosshair, Zap, Hand, Brain, Flame, HeartPulse, Route } from 'lucide-react';

const SHOOTING_IMG = 'https://customer-assets-4nw71qhi.emergentagent.net/job_elite-coaching-15/artifacts/ztpuiivj_Shooting.webp';
const NERVOUS_IMG = 'https://customer-assets-4nw71qhi.emergentagent.net/job_elite-coaching-15/artifacts/6mm4x94m_nervous%20system%20regulation.webp';
const CONSTRAINT_IMG = 'https://customer-assets-4nw71qhi.emergentagent.net/job_elite-coaching-15/artifacts/r5mul2pg_Constraint%20led%20game%20scenrios.webp';
const FINISHING_IMG = 'https://customer-assets-4nw71qhi.emergentagent.net/job_elite-coaching-15/artifacts/4gdbf93g_finishing%20%282%29.webp';
const BALL_HANDLING_IMG = 'https://customer-assets-4nw71qhi.emergentagent.net/job_elite-coaching-15/artifacts/e22t8vof_ball%20handling.webp';
const DECISION_IMG = 'https://customer-assets-4nw71qhi.emergentagent.net/job_elite-coaching-15/artifacts/bf3glrnj_decision%20making.webp';
const CONFIDENCE_IMG = 'https://customer-assets-4nw71qhi.emergentagent.net/job_elite-coaching-15/artifacts/5uhv4mtn_Confidence%20.webp';

const cards = [
  { icon: Crosshair, title: 'Shooting', copy: 'Develop a repeatable shot with range, consistency, and confidence.', image: SHOOTING_IMG },
  { icon: Zap, title: 'Finishing', copy: 'Score at all three levels with strength, touch, and creativity.', image: FINISHING_IMG },
  { icon: Hand, title: 'Ball Handling', copy: 'Handle pressure with control, create separation, and attack with purpose.', image: BALL_HANDLING_IMG },
  { icon: Brain, title: 'Decision-Making', copy: 'Read the floor, make the right play, and elevate your basketball IQ.', image: DECISION_IMG },
  { icon: Flame, title: 'Confidence', copy: 'Build unshakable belief through reps, results, and the right mindset.', image: CONFIDENCE_IMG },
  { icon: HeartPulse, title: 'Nervous System Regulation', copy: 'Stay calm, locked in, and perform under pressure when it matters most.', image: NERVOUS_IMG },
  { icon: Route, title: 'Constraint-Led Game Scenarios', copy: 'Train in realistic game scenarios that build skill, decisions, and adaptability.', image: CONSTRAINT_IMG },
];

const Card = ({ c, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-40px' }}
    transition={{ duration: 0.6, delay: index * 0.06 }}
    data-testid={`who-card-${index}`}
    className="group rounded-2xl border border-neutral-800 hover:border-gray-500 bg-neutral-950 overflow-hidden transition-all duration-300 hover:-translate-y-1 flex flex-col"
  >
    {/* Media */}
    <div className="relative aspect-[4/5] overflow-hidden bg-black">
      {c.image ? (
        <>
          <img
            src={c.image}
            alt={c.title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-neutral-950/40 to-transparent" />
        </>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-neutral-900 via-neutral-950 to-black">
          <c.icon className="w-16 h-16 text-gray-600" strokeWidth={1.2} />
          <div
            aria-hidden
            className="absolute inset-0 opacity-40"
            style={{
              backgroundImage:
                'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.06), transparent 55%)',
            }}
          />
        </div>
      )}
    </div>

    {/* Text */}
    <div className="p-5 sm:p-6 text-center flex flex-col flex-1">
      <div className="flex items-center justify-center gap-2 mb-2">
        <c.icon className="w-5 h-5 text-gray-300" strokeWidth={1.5} />
        <h3 className="font-display text-white text-base sm:text-lg uppercase leading-tight tracking-wide">
          {c.title}
        </h3>
      </div>
      <div className="w-8 h-0.5 bg-gray-400 mx-auto mt-1 mb-3" />
      <p className="text-gray-400 text-sm leading-relaxed">{c.copy}</p>
    </div>
  </motion.div>
);

export const WhoItsFor = () => {
  return (
    <section data-testid="who-its-for" className="bg-black py-16 sm:py-14 sm:py-16 lg:py-20 lg:py-24 px-6">
      <div className="container mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.7 }}
          className="text-center mb-14"
        >
          <p className="text-gray-400 font-bold text-xs tracking-[0.35em] uppercase mb-4">
            — The Dillon Blueprint —
          </p>
          <h2 className="font-display text-white text-4xl sm:text-6xl uppercase">Who This Is For</h2>
          <p className="text-gray-300 text-lg leading-relaxed max-w-xl mx-auto mt-5">
            Built for players who are committed to playing at the next level and serious about getting better.
          </p>
        </motion.div>

        {/* Row 1: 4 cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {cards.slice(0, 4).map((c, i) => (
            <Card key={c.title} c={c} index={i} />
          ))}
        </div>

        {/* Row 2: 3 cards centered */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-5 mt-5 lg:max-w-5xl lg:mx-auto">
          {cards.slice(4).map((c, i) => (
            <div
              key={c.title}
              className={i === 2 ? 'col-span-2 lg:col-span-1' : ''}
            >
              <Card c={c} index={i + 4} />
            </div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          data-testid="who-footer-bar"
          className="rounded-2xl border border-neutral-800 bg-neutral-950 p-7 mt-10 text-center"
        >
          <p className="text-white font-bold text-lg leading-relaxed max-w-2xl mx-auto">
            For players who want game-transfer skill development, structure, confidence, and real progress.
          </p>
        </motion.div>
      </div>
    </section>
  );
};
